import { BranchesGame, StemPosition } from './BranchesGame.js';
import { BranchesRenderer } from './BranchesRenderer.js';
import { isVortexGame } from '../../games/IVortexGame.js';
import { Edge, Position, edgeAdjacentCells, isRimEdge, manhattanDistance } from '../../core/GridGeometry.js';
import { BaseInput, InputCallback } from '../../input/BaseInput.js';
import { KeyboardControllable, FinePoint, FineTraceStep } from '../../input/KeyboardController.js';
import { fineToCell, fineToEdge } from '../../input/fineGeometry.js';

export class BranchesInput extends BaseInput<BranchesGame, BranchesRenderer> implements KeyboardControllable {
    private gateMode: boolean = false;

    constructor(
        game: BranchesGame,
        renderer: BranchesRenderer,
        canvas: HTMLCanvasElement,
        onUpdate: InputCallback
    ) {
        super(game, renderer, canvas, onUpdate);
        this.setupEventListeners();
    }

    setGateMode(enabled: boolean): void {
        this.gateMode = enabled;
    }

    protected setupEventListeners(): void {
        this.canvas.addEventListener('click', this.handleClick);
        this.canvas.addEventListener('contextmenu', this.handleRightClick);
    }

    protected removeEventListeners(): void {
        this.canvas.removeEventListener('click', this.handleClick);
        this.canvas.removeEventListener('contextmenu', this.handleRightClick);
    }

    private handleRightClick = (event: MouseEvent): void => {
        event.preventDefault();

        // While building a branch, right-click cancels it.
        if (this.game.isBuilding()) {
            this.game.cancelBuild();
            this.onUpdate();
            return;
        }

        if (this.gateMode) return;
        const { x, y } = this.getCoordinates(event);
        const edge = this.detector.detectEdge(x, y, 0.25, true);
        if (edge && !isRimEdge(edge)) {
            const cells = edgeAdjacentCells(edge);
            if (cells.length === 2 && this.game.hasSegment(cells[0], cells[1])) {
                this.game.toggleSegment(cells[0], cells[1]);
                this.onUpdate();
            }
        }
    };

    private handleClick = (e: MouseEvent): void => {
        const { x, y } = this.getCoordinates(e);

        // While building a branch, all clicks drive the build (cell-based).
        if (this.game.isBuilding()) {
            const cell = this.detector.detectCell(x, y);
            if (cell && this.game.buildClick({ row: cell.row, col: cell.col }) !== 'none') {
                this.onUpdate();
            }
            return;
        }

        if (this.gateMode && isVortexGame(this.game)) {
            const cell = this.detector.detectCell(x, y);
            if (cell && this.game.toggleVortexGate(cell)) {
                this.onUpdate();
            }
            return;
        }

        // Detect edges including rim edges
        const edge = this.detector.detectEdge(x, y, 0.25, true);
        if (edge) {
            if (isRimEdge(edge)) {
                // Rim edge -> stem placement
                const stemPos = this.edgeToStemPosition(edge);
                if (stemPos) {
                    this.game.toggleStem(stemPos);
                    this.onUpdate();
                }
            } else {
                // Internal edge -> segment placement
                const cells = edgeAdjacentCells(edge);
                if (cells.length === 2) {
                    this.game.toggleSegment(cells[0], cells[1]);
                    this.onUpdate();
                }
            }
        } else {
            const cell = this.detector.detectCell(x, y);
            if (!cell) return;
            const pos = { row: cell.row, col: cell.col };

            // Clicking a digit starts antenna-style branch building.
            if (this.game.getGrid().hasDigitAt(pos)) {
                if (this.game.startBuild(pos)) this.onUpdate();
                return;
            }

            // Fallback: cell-center click extends from an adjacent branch tip
            const tips = this.game.getEndpoints();
            const adjacent = tips.filter(([p]) => manhattanDistance(p, pos) === 1);
            if (adjacent.length === 1) {
                this.game.toggleSegment(adjacent[0][0], pos);
                this.onUpdate();
            }
        }
    };

    // ── Keyboard mode ─────────────────────────────────────────────────
    // Segments live on the edges *between* cells, so the cursor rides the half-grid
    // like Walls' and Flood's: a tap on an edge toggles that one segment, exactly as
    // clicking it does. The rim is in range too, because a rim edge is where the stem
    // goes. A tap on a digit starts an antenna build, and further taps drive it —
    // the same three things the mouse does, reachable the same way.

    usesFineCursor(): boolean {
        return true;
    }

    /** The stem sits on a rim edge, so the rim ring has to be in range. */
    allowsFineRim(): boolean {
        return true;
    }

    /** A tree doesn't wrap, so neither does the cursor: it stops at the boundary. */
    wrapsFineCursor(): boolean {
        return false;
    }

    /** A trace can start out on a rim edge and step in — planting the stem on the
     *  way, then carrying straight on into the tree. */
    tracesFromFineRim(): boolean {
        return true;
    }

    placeAtFine(p: FinePoint): boolean {
        if (this.gateMode) return false;

        // Mid-build, every tap drives the build, as every click does.
        if (this.game.isBuilding()) {
            const cell = fineToCell(p);
            return !!cell && this.game.buildClick(cell) !== 'none';
        }

        const edge = fineToEdge(this.game.getGrid(), p);
        if (edge) {
            if (isRimEdge(edge)) {
                const stem = this.edgeToStemPosition(edge);
                if (!stem) return false;
                this.game.toggleStem(stem);
                return true;
            }
            const cells = edgeAdjacentCells(edge);
            return cells.length === 2 && this.game.toggleSegment(cells[0], cells[1]);
        }

        // A cell centre: on a digit, start an antenna build.
        const cell = fineToCell(p);
        return !!cell && this.game.getGrid().hasDigitAt(cell) && this.game.startBuild(cell);
    }

    deleteAtFine(p: FinePoint): boolean {
        if (this.gateMode) return false;

        if (this.game.isBuilding()) {
            this.game.cancelBuild();
            return true;
        }

        const edge = fineToEdge(this.game.getGrid(), p);
        if (edge) {
            if (isRimEdge(edge)) {
                // Remove the stem only if it is the one sitting on this edge.
                const stem = this.edgeToStemPosition(edge);
                const cur = this.game.getStem();
                if (!stem || !cur || cur.side !== stem.side || cur.index !== stem.index) return false;
                this.game.toggleStem(stem);
                return true;
            }
            const cells = edgeAdjacentCells(edge);
            if (cells.length !== 2 || !this.game.hasSegment(cells[0], cells[1])) return false;
            return this.game.toggleSegment(cells[0], cells[1]);
        }

        // On a cell centre: clear every segment touching it.
        const cell = fineToCell(p);
        return !!cell && this.removeSegmentsAt(cell);
    }

    /**
     * A trace hop lays the segment it crosses — and a hop that **crosses the rim**,
     * either way, plants the stem on the rim edge it crossed instead. Running off
     * the grid reports the inside cell and an outward heading; starting outside and
     * stepping in reports the outside cell and an inward one. Both name the same
     * edge, so both are the same move.
     */
    traceFine({ cell, moveDir }: FineTraceStep): boolean {
        if (this.gateMode) return false;
        const grid = this.game.getGrid();
        const next = grid.move(cell, moveDir);
        const cellIn = grid.isValidPosition(cell);
        const nextIn = grid.isValidPosition(next);

        if (cellIn && nextIn) return this.game.toggleSegment(cell, next);
        if (cellIn === nextIn) return false;      // both outside: sliding along the rim
        const stem = this.stemForOutside(cellIn ? next : cell);
        if (!stem) return false;
        this.game.toggleStem(stem);
        return true;
    }

    /** The stem position on the rim edge a cell just outside the grid sits against. */
    private stemForOutside(outside: Position): StemPosition | null {
        const n = this.game.getGrid().size;
        const rowIn = outside.row >= 0 && outside.row < n;
        const colIn = outside.col >= 0 && outside.col < n;
        if (outside.row < 0 && colIn) return { side: 'top', index: outside.col };
        if (outside.row >= n && colIn) return { side: 'bottom', index: outside.col };
        if (outside.col < 0 && rowIn) return { side: 'left', index: outside.row };
        if (outside.col >= n && rowIn) return { side: 'right', index: outside.row };
        return null;
    }

    /** Remove every segment touching an interior cell. */
    private removeSegmentsAt(pos: Position): boolean {
        let changed = false;
        for (const [dr, dc] of [[-1, 0], [1, 0], [0, -1], [0, 1]] as const) {
            const nb = { row: pos.row + dr, col: pos.col + dc };
            if (this.game.getGrid().isValidPosition(nb) && this.game.hasSegment(pos, nb)) {
                this.game.toggleSegment(pos, nb);
                changed = true;
            }
        }
        return changed;
    }

    /**
     * Convert a rim edge to a stem position.
     */
    private edgeToStemPosition(edge: Edge): StemPosition | null {
        const gridSize = this.game.getGrid().size;
        if (edge.orient === 'h') {
            if (edge.pa.row === 0) {
                // Top rim
                return { side: 'top', index: edge.pa.col };
            } else if (edge.pa.row === gridSize) {
                // Bottom rim
                return { side: 'bottom', index: edge.pa.col };
            }
        } else {
            if (edge.pa.col === 0) {
                // Left rim
                return { side: 'left', index: edge.pa.row };
            } else if (edge.pa.col === gridSize) {
                // Right rim
                return { side: 'right', index: edge.pa.row };
            }
        }
        return null;
    }

    /** `X` goes to the stem's rim edge, or — before one is planted — to the rim edge
     *  facing the first digit, on whichever of the four sides lies nearest to it. */
    fineCursorOrigin(): FinePoint | null {
        const n = this.game.getGrid().size;
        const stem = this.game.getStem();
        if (stem) return this.stemFinePoint(stem);

        const grid = this.game.getGrid();
        const d = grid.getDigitPosition(grid.minDigit);
        if (!d) return null;
        const candidates: Array<{ stem: StemPosition; dist: number }> = [
            { stem: { side: 'top', index: d.col }, dist: d.row + 1 },
            { stem: { side: 'bottom', index: d.col }, dist: n - d.row },
            { stem: { side: 'left', index: d.row }, dist: d.col + 1 },
            { stem: { side: 'right', index: d.row }, dist: n - d.col },
        ];
        return this.stemFinePoint(candidates.reduce((a, b) => (b.dist < a.dist ? b : a)).stem);
    }

    /** A stem position as the half-grid point of the rim edge it sits on. */
    private stemFinePoint(stem: StemPosition): FinePoint {
        const n = this.game.getGrid().size;
        switch (stem.side) {
            case 'top': return { r: 0, c: 2 * stem.index + 1 };
            case 'bottom': return { r: 2 * n, c: 2 * stem.index + 1 };
            case 'left': return { r: 2 * stem.index + 1, c: 0 };
            case 'right': return { r: 2 * stem.index + 1, c: 2 * n };
        }
    }

}
